// Central GA4 event helpers. Typed wrappers around window.gtag so call sites
// stay clean and parameter names stay consistent across the codebase.
// Used by the wizard, contract pages, blog CTAs and PDF landing pages.

function track(name, params) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", name, params || {});
  }
}

// Generic escape hatch for ad-hoc events. Prefer typed helpers below.
export function trackEvent(name, params) {
  track(name, params);
}

// --- Contract picker (homepage) ---

export function trackSelectContract(contractType) {
  track("select_contract", { contract_type: contractType });
}

// --- Flow choice (contract page) ---
// flow_choice_viewed fires when the choice screen renders; flow_choice_clicked
// fires when the user picks a flow (online_wizard | model_pdf).

export function trackFlowChoiceViewed(contractType) {
  track("flow_choice_viewed", { contract_type: contractType });
}

export function trackFlowChoiceClicked(contractType, flow) {
  track("flow_choice_clicked", { contract_type: contractType, flow });
}

// --- Wizard lifecycle ---

export function trackWizardStarted(contractType, entrySource) {
  track("wizard_started", {
    contract_type: contractType,
    entry_source: entrySource,
  });
}

export function trackWizardStep(contractType, stepNumber, stepName, documentType) {
  track("wizard_step", {
    contract_type: contractType,
    step_number: stepNumber,
    step_name: stepName,
    document_type: documentType,
  });
}

export function trackWizardStepCompleted(contractType, stepNumber, stepName, timeOnStepSeconds, documentType) {
  track("wizard_step_completed", {
    contract_type: contractType,
    step_number: stepNumber,
    step_name: stepName,
    time_on_step_seconds: timeOnStepSeconds,
    document_type: documentType,
  });
}

// Terminal completion event, fired once when the user finishes the wizard
// (final step reached + first download). Unlike wizard_step_completed, it does
// not depend on a fixed step_number, so a single funnel can measure completion
// across contract types with different step counts. step_number is the final
// step reached, for reference only.
export function trackWizardCompleted(contractType, documentType, stepNumber) {
  track("wizard_completed", {
    contract_type: contractType,
    document_type: documentType,
    step_number: stepNumber,
  });
}

// step_number is the last step the user reached. Named step_number (not
// last_step_completed) so it matches every other wizard event.
export function trackWizardAbandoned(contractType, stepNumber, trigger, documentType) {
  track("wizard_abandoned", {
    contract_type: contractType,
    step_number: stepNumber,
    trigger,
    document_type: documentType,
  });
}

export function trackCtaVisible(contractType, stepNumber) {
  track("cta_visible", {
    contract_type: contractType,
    step_number: stepNumber,
  });
}

// --- Download events ---

export function trackDownloadContract(contractType, documentType) {
  track("download_contract", {
    contract_type: contractType,
    document_type: documentType,
  });
}

// `source` describes WHERE the user came from when they clicked download
// (pdf_landing_page | choice_screen_text_link | blog_inline | other).
// `ctaLocation` is finer-grained (hero, preview_image, ...).
export function trackModelPdfDownload(contractType, source, ctaLocation, pageSlug) {
  const params = {
    contract_type: contractType,
    source: source || "other",
  };
  if (ctaLocation) params.cta_location = ctaLocation;
  if (pageSlug) params.page_slug = pageSlug;
  track("model_pdf_download", params);
}

export function trackModelToGeneratorClick(pageSlug, contractType, ctaLocation) {
  track("model_to_generator_click", {
    page_slug: pageSlug,
    contract_type: contractType,
    cta_location: ctaLocation,
  });
}

// --- Blog CTAs ---

export function trackBlogCtaClick(sourceArticle, contractType, ctaPosition) {
  const params = { source_article: sourceArticle };
  // Omitted rather than sent empty when the CTA points somewhere that is not a
  // contract route, so the dimension stays clean.
  if (contractType) params.contract_type = contractType;
  if (ctaPosition) params.cta_position = ctaPosition;
  track("blog_cta_click", params);
}

// --- Helpers ---

// Classifies the page entry to attribute wizard starts to traffic sources.
// Categories match GA4 dimensions used in funnel reports.
export function getEntrySource() {
  if (typeof window === "undefined") return "direct";

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") === "blog") return "blog_cta";
    if (params.get("from") === "pdf_landing") return "pdf_landing_upsell";

    const ref = document.referrer || "";
    if (!ref) return "direct";

    const refUrl = new URL(ref);
    const host = refUrl.hostname;
    const ownHost = window.location.hostname;

    if (host === ownHost) {
      if (refUrl.pathname.startsWith("/blog")) return "blog_cta";
      if (/\/model-/.test(refUrl.pathname)) return "pdf_landing_upsell";
      return "internal";
    }

    if (/google|bing|yahoo|duckduckgo/i.test(host)) return "organic";
    return "referral";
  } catch (e) {
    return "direct";
  }
}
